// 与 server/internal/improtocol 严格对齐的协议类型。
// 任何字段改动都要双侧同步——这是单仓 monorepo 的便利。

export const FrameType = {
  MsgSend: 'msg.send',
  MsgAck: 'msg.ack',
  MsgRecv: 'msg.recv',
  MsgRecall: 'msg.recall',
  MsgRead: 'msg.read',
  CtrlPing: 'ctrl.ping',
  CtrlPong: 'ctrl.pong',
  CtrlQueueHeartbeat: 'ctrl.queue_heartbeat',
  CtrlTyping: 'ctrl.typing',
  SysNotice: 'sys.notice',
  SysError: 'sys.error',
} as const;

export type FrameType = (typeof FrameType)[keyof typeof FrameType];

export type AckState = 'queued' | 'persisted' | 'delivered' | 'read' | 'failed';

export type ErrorCode =
  | 'invalid_frame'
  | 'unauthorized'
  | 'text_too_long'
  | 'unsupported_type'
  | 'rate_limited'
  | 'payload_invalid'
  | 'internal_error';

export interface Frame {
  id: string;
  type: FrameType;
  ts?: number;
  from?: string;
  to?: string;
  convId?: string;
  payload?: unknown;
}

export interface MsgSendPayload {
  contentType: 'text' | 'image' | 'video' | 'file' | 'emoji' | 'form' | 'card';
  content: unknown;
  quoteOfMsgId?: string;
}

export interface MsgAckPayload {
  state: AckState;
  code?: ErrorCode;
  reason?: string;
}

export interface MsgRecvPayload extends MsgSendPayload {
  msgId: string;
  /** 服务端透传的客户端发送 ID（self-echo 时用于对账） */
  clientMsgId?: string;
  senderKey: string;
  createdAt: number;
  /** true 表示这是发送方自己的回环帧（让发送方把 clientMsgId 对齐为 server msgId） */
  echo?: boolean;
  /** 被引用消息的预览（quoteOfMsgId 有效时由服务端 join 注入） */
  quote?: QuotePreviewFrame;
}

// MsgRecallPayload 对齐 server/internal/improtocol MsgRecallPayload。
// 客户端发起仅需 msgId；服务端下行带 recalledAt（毫秒）。
export interface MsgRecallPayload {
  msgId: string;
  reason?: string;
  recalledAt?: number;
}

export interface QuotePreviewFrame {
  msgId: string;
  senderType: 'visitor' | 'agent' | 'bot' | 'system';
  senderName?: string;
  contentType: string;
  content?: unknown;
  recalled?: boolean;
  createdAt: number;
}

export interface SysErrorPayload {
  code: ErrorCode;
  message: string;
}

export type NoticeCode =
  | 'server_restart'
  | 'server_shutdown'
  | 'session_replaced'
  | 'conv_idle_timeout'
  | 'conv_idle_warning'
  | 'queue_position'
  | 'queue_timeout'
  | 'conv_transferred'
  | 'conv_ended'
  | 'read_receipt_changed';

export interface SysNoticePayload {
  code: NoticeCode;
  message?: string;
  reconnectAfterMs?: number;
  /** 仅 code='queue_position' 时使用 */
  position?: number;
  total?: number;
  etaSeconds?: number;
  /** 仅 code='conv_idle_warning' 时使用 */
  discMinutes?: number;
  /** 仅 code='conv_transferred' 时使用：转接后的新坐席显示名 */
  targetAgentName?: string;
  /** 仅 code='conv_transferred' 时使用：转接后的新坐席 userKey，用作 ctrl.typing 的 to */
  agentUserKey?: string;
  /** 仅 code='read_receipt_changed' 时使用：租户已读回执开关的新值 */
  readReceiptEnabled?: boolean;
}

export type TypingState = 'start' | 'stop';

/** CtrlTypingPayload.preview 的字符数上限，与服务端 improtocol.TypingPreviewMaxRunes 对齐。
 *  客户端先截断只为省流量；服务端仍会独立截断一次。 */
export const TYPING_PREVIEW_MAX_RUNES = 200;

export interface CtrlTypingPayload {
  state: TypingState;
  /** 输入框里尚未发出的文本，供坐席实时预览。仅访客→坐席单向，且受租户开关
   *  typingPreviewEnabled 控制；服务端会截断到 200 字符并清空坐席上行的该字段。 */
  preview?: string;
}

export interface MsgReadPayload {
  lastReadMsgId: string;
}

export type Role = 'visitor' | 'agent';

export function formatUserKey(tenantId: string, role: Role, userId: string): string {
  const prefix = role === 'agent' ? 'a' : 'v';
  return `${tenantId}:${prefix}${userId}`;
}
